// Expandable mission detail shared by the captain's Mission tab and the base mission board: description, a checklist of
// steps with live progress, and an "Add waypoint" button that sets the nav room's waypoint to the objective's location.
import type { ControlInput, GameSnapshot, MissionObjective, MissionStep } from '../../shared/types.js';
import { mapPointToSimulation, simulationToMapPoint } from '../../shared/spatial.js';

type NavWaypoint = GameSnapshot['navWaypoint'];
export type WaypointRequest = { simulationX: number; simulationZ: number; depth?: number };

/** Waypoint and objective count as "the same place" within this many map metres. */
const WAYPOINT_MATCH_METERS = 2;

export type MissionVisualState = 'done' | 'active' | 'pending';

/** done / active / pending for each listed objective. Uses the server's `active` flag when present (the list omits hidden missions, so the index can drift). */
export const missionStates = (missions: MissionObjective[], activeMissionIndex: number): MissionVisualState[] => {
  const flagged = missions.findIndex((m) => m.active);
  const activeIdx = flagged >= 0 ? flagged : activeMissionIndex;
  return missions.map((m, i) => {
    if (m.optional) return m.done ? 'done' : 'pending';
    return i < activeIdx ? 'done' : i === activeIdx ? 'active' : 'pending';
  });
};

/** The nav waypoint that points at an objective's location (map space -> simulation space via the shared helpers), or null. */
export const missionWaypointRequest = (objective: MissionObjective): WaypointRequest | null => {
  const loc = objective.location;
  if (!loc) return null;
  const sim = mapPointToSimulation({ x: loc.x, z: loc.z });
  return { simulationX: sim.x, simulationZ: sim.y, ...(typeof loc.depth === 'number' ? { depth: loc.depth } : {}) };
};

/** Is the nav room's waypoint already at this objective's location? */
export const isWaypointAtMission = (objective: MissionObjective, waypoint: NavWaypoint | undefined): boolean => {
  const loc = objective.location;
  if (!loc || !waypoint) return false;
  const point = simulationToMapPoint({ x: waypoint.simulationX, y: waypoint.simulationZ });
  if (Math.hypot(point.x - loc.x, point.z - loc.z) > WAYPOINT_MATCH_METERS) return false;
  if (typeof loc.depth === 'number' && typeof waypoint.depth === 'number') return Math.abs(waypoint.depth - loc.depth) <= WAYPOINT_MATCH_METERS;
  return true;
};

const progressText = (step: MissionStep): string => {
  const p = step.progress;
  if (!p) return '';
  return `${p.current}/${p.target}${p.unit === 's' ? ' s' : ''}`;
};

const renderSteps = (steps: MissionStep[], nested = false): HTMLUListElement => {
  const list = document.createElement('ul');
  list.className = nested ? 'md-steps md-steps-nested' : 'md-steps';
  for (const step of steps) {
    const li = document.createElement('li');
    li.className = `md-step${step.done ? ' done' : ''}`;
    const tick = document.createElement('span');
    tick.className = 'md-step-tick';
    tick.textContent = step.done ? '✓' : '○';
    const text = document.createElement('span');
    text.className = 'md-step-text';
    text.textContent = step.text;
    li.append(tick, text);
    const progress = progressText(step);
    if (progress) {
      const counter = document.createElement('span');
      counter.className = 'md-step-progress';
      counter.textContent = progress;
      li.append(counter);
    }
    list.append(li);
    if (step.children?.length) list.append(renderSteps(step.children, true));
  }
  return list;
};

/** Dev-only "Skip mission" button: completes this mission (and any before it) through the `skip_mission` control action. */
export const buildSkipMissionButton = (missionId: string, emit: (input: ControlInput) => void): HTMLButtonElement => {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'md-skip-btn';
  button.textContent = 'Skip mission (dev)';
  button.title = 'Developer tools: completes this mission now, with its rewards and follow-up actions.';
  button.addEventListener('click', (event) => {
    event.stopPropagation();
    emit({ action: 'skip_mission', missionId });
  });
  return button;
};

export type MissionDetailOptions = {
  objective: MissionObjective;
  /** The nav room's current waypoint (snapshot.navWaypoint). */
  navWaypoint: NavWaypoint | undefined;
  /** Set (a request) or clear (null) the nav room's waypoint. Omit to hide the button. */
  setNavWaypoint?: (waypoint: WaypointRequest | null) => void;
};

/** The expanded body of a mission row. */
export const buildMissionDetail = ({ objective, navWaypoint, setNavWaypoint }: MissionDetailOptions): HTMLElement => {
  const wrap = document.createElement('div');
  wrap.className = 'md-detail';
  if (objective.description) {
    const desc = document.createElement('div');
    desc.className = 'md-desc';
    desc.textContent = objective.description;
    wrap.append(desc);
  }
  if (objective.steps?.length) {
    const heading = document.createElement('div');
    heading.className = 'md-heading';
    heading.textContent = 'Objectives';
    wrap.append(heading, renderSteps(objective.steps));
  }
  const request = missionWaypointRequest(objective);
  if (request && setNavWaypoint) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'md-waypoint-btn';
    const set = isWaypointAtMission(objective, navWaypoint);
    button.classList.toggle('set', set);
    button.textContent = set ? 'Waypoint set ✓ (tap to clear)' : 'Add waypoint';
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      const nowSet = button.classList.contains('set');
      setNavWaypoint(nowSet ? null : request);
      button.classList.toggle('set', !nowSet);
      button.textContent = nowSet ? 'Add waypoint' : 'Waypoint set ✓ (tap to clear)';
    });
    wrap.append(button);
  }
  return wrap;
};
